import React, { useEffect } from 'react';
import { Navbar } from '../../../components/feature/Navbar';
import { Footer } from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function VancouverWebDesignTipsPage() {
  useEffect(() => {
    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://businesswebsitepro.com/blog/vancouver-web-design-tips');

    // SEO Meta Tags - Optimized for Vancouver local search
    document.title = 'Web Design Tips for Small Businesses in Vancouver, BC | 2024 Guide | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover essential web design tips for Vancouver small businesses. Learn how to create a professional website that attracts local customers in British Columbia. Expert advice from Vancouver web designers serving BC since 2020.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Vancouver web design, BC small business websites, Vancouver web designer, British Columbia web development, local business website Vancouver, web design tips Vancouver, small business online presence BC');
    }

    // Geo-specific meta tags
    let geoRegion = document.querySelector('meta[name="geo.region"]');
    if (!geoRegion) {
      geoRegion = document.createElement('meta');
      geoRegion.setAttribute('name', 'geo.region');
      document.head.appendChild(geoRegion);
    }
    geoRegion.setAttribute('content', 'CA-BC');

    let geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (!geoPlacename) {
      geoPlacename = document.createElement('meta');
      geoPlacename.setAttribute('name', 'geo.placename');
      document.head.appendChild(geoPlacename);
    }
    geoPlacename.setAttribute('content', 'Vancouver, British Columbia');

    let geoPosition = document.querySelector('meta[name="geo.position"]');
    if (!geoPosition) {
      geoPosition = document.createElement('meta');
      geoPosition.setAttribute('name', 'geo.position');
      document.head.appendChild(geoPosition);
    }
    geoPosition.setAttribute('content', '49.2827;-123.1207');

    let icbm = document.querySelector('meta[name="ICBM"]');
    if (!icbm) {
      icbm = document.createElement('meta');
      icbm.setAttribute('name', 'ICBM');
      document.head.appendChild(icbm);
    }
    icbm.setAttribute('content', '49.2827, -123.1207');

    // Scroll to top
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Schema.org Article structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Web Design Tips for Small Businesses in Vancouver, BC",
          "description": "Essential web design tips for Vancouver small businesses to create professional websites that attract local customers",
          "image": "https://public.readdy.ai/ai/img_res/8f5dceb9b94d47922df9217cb063b3e1.jpg",
          "author": {
            "@type": "Organization",
            "name": "BusinessWebsitePro",
            "url": "https://businesswebsitepro.com"
          },
          "publisher": {
            "@type": "Organization",
            "name": "BusinessWebsitePro",
            "logo": {
              "@type": "ImageObject",
              "url": "https://businesswebsitepro.com/logo.png"
            }
          },
          "datePublished": "2024-01-15",
          "dateModified": "2024-01-15",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://businesswebsitepro.com/blog/vancouver-web-design-tips"
          },
          "about": {
            "@type": "Place",
            "name": "Vancouver",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Vancouver",
              "addressRegion": "BC",
              "addressCountry": "CA"
            }
          }
        })}
      </script>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="max-w-4xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-gray-600 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-teal-600 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/blog" className="hover:text-teal-600 transition-colors cursor-pointer">Blog</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium">Vancouver Web Design Tips</span>
          </nav>

          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-4 py-2 rounded-full">
              Local Business Tips
            </span>
            <span className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full">
              Vancouver, BC
            </span>
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Web Design Tips for Small Businesses in Vancouver, BC
          </h1>

          <div className="flex items-center gap-6 text-gray-600 mb-8">
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line"></i>
              <span>January 15, 2024</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line"></i>
              <span>8 min read</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-map-pin-line"></i>
              <span>Vancouver, BC</span>
            </div>
          </div>

          <div className="w-full h-96 rounded-2xl overflow-hidden shadow-xl mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20professional%20website%20design%20on%20computer%20screen%20in%20Vancouver%20office%20with%20city%20skyline%20view%2C%20clean%20minimalist%20workspace%2C%20natural%20lighting%2C%20professional%20photography%20style%2C%20mountains%20in%20background&width=1200&height=600&seq=vancouver-hero-main&orientation=landscape"
              alt="Professional web design workspace in Vancouver with city skyline"
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              As a <strong>small business owner in Vancouver, BC</strong>, having a professional website is no longer optional—it's essential. With over 2.5 million people in the Greater Vancouver area and a thriving business ecosystem, your online presence can make or break your success in this competitive market.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Whether you're running a café in Gastown, a boutique in Kitsilano, or a service business in Richmond, these <strong>web design tips for Vancouver businesses</strong> will help you create a website that attracts local customers and drives growth.
            </p>
          </div>

          {/* Tip 1 */}
          <section className="mb-16">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-teal-700">1</span>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Optimize for Local Vancouver Search</h2>
              </div>
            </div>
            
            <div className="pl-16">
              <p className="text-lg text-gray-700 mb-4">
                When Vancouver residents search for services "near me" or "in Vancouver," you want your business to appear. Here's how:
              </p>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Local SEO Checklist for Vancouver Businesses:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                    <span className="text-gray-700"><strong>Include "Vancouver" and "BC"</strong> in your page titles, headings, and content naturally</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                    <span className="text-gray-700"><strong>Add your complete address</strong> in the footer (street, city, postal code)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                    <span className="text-gray-700"><strong>Mention Vancouver neighborhoods</strong> you serve (Downtown, Kitsilano, Richmond, Burnaby, Surrey)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                    <span className="text-gray-700"><strong>Create a Google Business Profile</strong> and link it to your website</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-1"></i>
                    <span className="text-gray-700"><strong>Add schema markup</strong> with your Vancouver location data</span>
                  </li>
                </ul>
              </div>

              <div className="w-full h-64 rounded-xl overflow-hidden shadow-lg mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Google%20Maps%20showing%20Vancouver%20BC%20location%20pin%20on%20smartphone%20screen%2C%20local%20business%20search%20results%2C%20modern%20clean%20interface%2C%20professional%20photography&width=800&height=400&seq=vancouver-local-seo&orientation=landscape"
                  alt="Local SEO optimization for Vancouver businesses"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-xl">
                <p className="text-gray-800">
                  <strong>💡 Pro Tip:</strong> Vancouver customers often search for "[service] near me" or "[service] in Vancouver." Make sure these phrases appear naturally in your content, especially on your homepage and service pages.
                </p>
              </div>
            </div>
          </section>

          {/* Tip 2 */}
          <section className="mb-16">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-teal-700">2</span>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Mobile-First Design is Critical in Vancouver</h2>
              </div>
            </div>
            
            <div className="pl-16">
              <p className="text-lg text-gray-700 mb-4">
                Over 70% of Vancouver residents use smartphones to search for local businesses. If your website isn't mobile-friendly, you're losing customers to competitors.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-close-line text-2xl text-red-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">❌ Don't Do This:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Tiny text that requires zooming</li>
                    <li>• Buttons too small to tap</li>
                    <li>• Horizontal scrolling</li>
                    <li>• Slow loading images</li>
                    <li>• Pop-ups that cover content</li>
                  </ul>
                </div>

                <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-check-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">✅ Do This Instead:</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Large, readable fonts (16px+)</li>
                    <li>• Thumb-friendly tap targets</li>
                    <li>• Single-column layouts</li>
                    <li>• Optimized, fast-loading images</li>
                    <li>• Easy-to-close modals</li>
                  </ul>
                </div>
              </div>

              <div className="w-full h-64 rounded-xl overflow-hidden shadow-lg mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=responsive%20mobile%20website%20design%20on%20smartphone%20and%20tablet%20screens%2C%20modern%20clean%20interface%2C%20Vancouver%20coffee%20shop%20website%20example%2C%20professional%20mockup&width=800&height=400&seq=vancouver-mobile-design&orientation=landscape"
                  alt="Mobile-responsive web design for Vancouver businesses"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          {/* Tip 3 */}
          <section className="mb-16">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-teal-700">3</span>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Showcase Your Vancouver Location</h2>
              </div>
            </div>
            
            <div className="pl-16">
              <p className="text-lg text-gray-700 mb-4">
                Vancouver customers prefer supporting local businesses. Make your local presence obvious:
              </p>

              <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-8 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Ways to Highlight Your Vancouver Location:</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Embed Google Maps</h4>
                      <p className="text-gray-700 text-sm">Show your exact Vancouver location with an interactive map</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-image-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Local Photos</h4>
                      <p className="text-gray-700 text-sm">Use images of Vancouver landmarks, your storefront, or team</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-star-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Local Testimonials</h4>
                      <p className="text-gray-700 text-sm">Feature reviews from Vancouver customers with their neighborhoods</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-community-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Service Areas</h4>
                      <p className="text-gray-700 text-sm">List Vancouver neighborhoods you serve (Yaletown, Coal Harbour, etc.)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full h-64 rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Vancouver%20BC%20cityscape%20with%20mountains%20and%20harbor%2C%20local%20business%20storefront%20with%20welcome%20sign%2C%20professional%20photography%2C%20bright%20natural%20lighting&width=800&height=400&seq=vancouver-location&orientation=landscape"
                  alt="Vancouver local business location showcase"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          {/* Tip 4 */}
          <section className="mb-16">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-teal-700">4</span>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Fast Loading Speed Matters in BC's Competitive Market</h2>
              </div>
            </div>
            
            <div className="pl-16">
              <p className="text-lg text-gray-700 mb-4">
                Vancouver customers won't wait for slow websites. Google reports that <strong>53% of mobile users abandon sites that take over 3 seconds to load</strong>.
              </p>

              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-6">
                <div className="flex items-start gap-4">
                  <i className="ri-speed-line text-4xl text-yellow-600"></i>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Speed Optimization Tips:</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li>• <strong>Compress images</strong> before uploading (use WebP format)</li>
                      <li>• <strong>Enable caching</strong> to store data locally</li>
                      <li>• <strong>Minimize code</strong> (remove unnecessary CSS/JavaScript)</li>
                      <li>• <strong>Use a CDN</strong> (Content Delivery Network) for faster loading</li>
                      <li>• <strong>Choose quality hosting</strong> with servers in Canada or Western US</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 text-white rounded-xl p-6">
                <p className="text-lg mb-2">
                  <strong>⚡ Target Loading Speed:</strong>
                </p>
                <p className="text-3xl font-bold text-teal-400 mb-2">&lt; 2 seconds</p>
                <p className="text-gray-300">
                  Test your site speed at <a href="https://pagespeed.web.dev/" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:underline cursor-pointer">PageSpeed Insights</a>
                </p>
              </div>
            </div>
          </section>

          {/* Tip 5 */}
          <section className="mb-16">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-teal-700">5</span>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Clear Call-to-Actions for Vancouver Customers</h2>
              </div>
            </div>
            
            <div className="pl-16">
              <p className="text-lg text-gray-700 mb-6">
                Make it crystal clear what you want visitors to do. Vancouver customers appreciate direct, action-oriented messaging.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white border-2 border-teal-500 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-phone-line text-3xl text-teal-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Phone Number</h3>
                  <p className="text-gray-700 text-sm mb-4">Prominently display with Vancouver area code (604/778)</p>
                  <button className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                    Call Now
                  </button>
                </div>

                <div className="bg-white border-2 border-teal-500 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-calendar-line text-3xl text-teal-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Book Appointment</h3>
                  <p className="text-gray-700 text-sm mb-4">Easy online booking for Vancouver clients</p>
                  <button className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                    Book Now
                  </button>
                </div>

                <div className="bg-white border-2 border-teal-500 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-mail-line text-3xl text-teal-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Get Quote</h3>
                  <p className="text-gray-700 text-sm mb-4">Free consultation for BC businesses</p>
                  <button className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                    Get Quote
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Tip 6 */}
          <section className="mb-16">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-teal-700">6</span>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Build Trust with Vancouver Social Proof</h2>
              </div>
            </div>
            
            <div className="pl-16">
              <p className="text-lg text-gray-700 mb-6">
                Vancouver customers trust businesses with proven track records. Show them you're established and reliable:
              </p>

              <div className="space-y-4 mb-6">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-start gap-4">
                    <i className="ri-star-fill text-3xl text-yellow-500"></i>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Customer Reviews</h3>
                      <p className="text-gray-700">Display Google reviews, testimonials from Vancouver clients, and star ratings prominently on your homepage.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-start gap-4">
                    <i className="ri-shield-check-line text-3xl text-teal-600"></i>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Certifications &amp; Memberships</h3>
                      <p className="text-gray-700">Show BC business licenses, industry certifications, or memberships in Vancouver business associations.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-start gap-4">
                    <i className="ri-trophy-line text-3xl text-orange-500"></i>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Awards &amp; Recognition</h3>
                      <p className="text-gray-700">Highlight any "Best of Vancouver" awards, media mentions, or industry recognition you've received.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                  <div className="flex items-start gap-4">
                    <i className="ri-team-line text-3xl text-blue-600"></i>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Team Photos</h3>
                      <p className="text-gray-700">Show real photos of your Vancouver team. People trust businesses with faces, not stock photos.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Vancouver-Specific Bonus Tips */}
          <section className="mb-16">
            <div className="bg-gradient-to-br from-blue-600 to-teal-600 text-white rounded-2xl p-8 md:p-12">
              <h2 className="text-3xl font-bold mb-6">🌟 Bonus: Vancouver-Specific Web Design Considerations</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-3">🌧️ Weather-Aware Design</h3>
                  <p className="text-white/90">
                    Vancouver's rainy climate means people browse indoors often. Ensure your site works well on all devices and has cozy, inviting visuals.
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-3">🌍 Multilingual Options</h3>
                  <p className="text-white/90">
                    Vancouver is multicultural. Consider adding Chinese, Punjabi, or other language options if your target market includes non-English speakers.
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-3">🚇 Transit-Friendly Info</h3>
                  <p className="text-white/90">
                    Many Vancouverites use public transit. Include SkyTrain/bus information if you have a physical location.
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-3">♻️ Sustainability Messaging</h3>
                  <p className="text-white/90">
                    Vancouver values eco-consciousness. Highlight any green practices or sustainable business operations.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-16">
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to Build Your Vancouver Business Website?</h2>
              <p className="text-lg text-gray-700 mb-6">
                Creating a professional website for your Vancouver small business doesn't have to be complicated or expensive. By following these web design tips, you'll have a site that attracts local customers, ranks well in Google searches, and helps your business grow in BC's competitive market.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                At <strong>BusinessWebsitePro</strong>, we specialize in creating beautiful, high-performing websites for Vancouver and British Columbia businesses. We understand the local market and know what works for BC companies.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/contact" 
                  className="bg-teal-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap inline-block"
                >
                  Get Your Free Quote
                </Link>
                <Link 
                  to="/portfolio" 
                  className="bg-white text-teal-600 border-2 border-teal-600 px-8 py-4 rounded-xl font-semibold hover:bg-teal-50 transition-all duration-300 cursor-pointer whitespace-nowrap inline-block"
                >
                  View Vancouver Projects
                </Link>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <details className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                <summary className="text-lg font-bold text-gray-900 cursor-pointer">
                  How much does a website cost for a small business in Vancouver?
                </summary>
                <p className="text-gray-700 mt-4">
                  Website costs for Vancouver small businesses typically range from $499 for a basic starter site to $1,999+ for a full-featured business website. At BusinessWebsitePro, we offer transparent pricing with no upfront payment required—you only pay when you're 100% satisfied with your site.
                </p>
              </details>

              <details className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                <summary className="text-lg font-bold text-gray-900 cursor-pointer">
                  Do I need a web designer in Vancouver, or can I build it myself?
                </summary>
                <p className="text-gray-700 mt-4">
                  While DIY website builders exist, hiring a professional Vancouver web designer saves you time and ensures your site is optimized for local search, mobile devices, and conversions. A professional site typically generates better ROI and looks more credible to Vancouver customers.
                </p>
              </details>

              <details className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                <summary className="text-lg font-bold text-gray-900 cursor-pointer">
                  How long does it take to build a website for a Vancouver business?
                </summary>
                <p className="text-gray-700 mt-4">
                  Most small business websites in Vancouver can be completed in 1-4 weeks, depending on complexity. At BusinessWebsitePro, our typical turnaround is 1-2 weeks for starter packages and 2-4 weeks for professional/enterprise sites.
                </p>
              </details>

              <details className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                <summary className="text-lg font-bold text-gray-900 cursor-pointer">
                  Will my website work on mobile phones?
                </summary>
                <p className="text-gray-700 mt-4">
                  Absolutely! All modern websites must be mobile-responsive. At BusinessWebsitePro, every site we build is fully optimized for smartphones, tablets, and desktops—ensuring Vancouver customers can access your site from any device.
                </p>
              </details>

              <details className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
                <summary className="text-lg font-bold text-gray-900 cursor-pointer">
                  Can you help my Vancouver business rank on Google?
                </summary>
                <p className="text-gray-700 mt-4">
                  Yes! All our websites include basic SEO optimization for Vancouver local search. We implement proper meta tags, schema markup, mobile optimization, and local SEO best practices to help your business appear in Google searches for "near me" and Vancouver-specific queries.
                </p>
              </details>
            </div>
          </section>

          {/* Related Articles */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/blog" className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-teal-500 hover:shadow-lg transition-all duration-300 cursor-pointer">
                <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-teal-100 flex items-center justify-center">
                  <i className="ri-article-line text-6xl text-teal-600"></i>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">More Web Design Tips</h3>
                  <p className="text-gray-600 text-sm">Explore our blog for more insights</p>
                </div>
              </Link>

              <Link to="/services" className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-teal-500 hover:shadow-lg transition-all duration-300 cursor-pointer">
                <div className="w-full h-48 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                  <i className="ri-service-line text-6xl text-purple-600"></i>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Our Services</h3>
                  <p className="text-gray-600 text-sm">See what we offer Vancouver businesses</p>
                </div>
              </Link>

              <Link to="/pricing" className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-teal-500 hover:shadow-lg transition-all duration-300 cursor-pointer">
                <div className="w-full h-48 bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-6xl text-orange-600"></i>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Pricing Packages</h3>
                  <p className="text-gray-600 text-sm">Transparent pricing for BC businesses</p>
                </div>
              </Link>
            </div>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}
